import React, { useState, useEffect } from 'react';

export default function FleetPDailyRecords() {
    // State
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [kmRecords, setKmRecords] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load records from localStorage
    useEffect(() => {
        setLoading(true);
        
        // Load attendance records
        const savedAttendance = localStorage.getItem('fleetp_attendance_records');
        if (savedAttendance) {
            setAttendanceRecords(JSON.parse(savedAttendance));
        }
        
        // Load KM records
        const savedKm = localStorage.getItem('fleetp_km_records');
        if (savedKm) {
            setKmRecords(JSON.parse(savedKm));
        }
        
        setLoading(false);
    }, []);

    // Get records for selected date
    const getDailyAttendance = () => {
        return attendanceRecords.filter(r => r.date === selectedDate);
    };

    const getDailyKm = () => {
        return kmRecords.filter(r => r.date === selectedDate);
    };

    // Calculate daily stats
    const getStats = () => {
        const dailyAttendance = getDailyAttendance();
        const dailyKm = getDailyKm();
        
        return {
            totalDrivers: new Set([...dailyAttendance.map(r => r.driverId), ...dailyKm.map(r => r.driverId)]).size,
            totalKm: dailyKm.reduce((sum, r) => sum + r.totalKm, 0),
            checkedIn: dailyAttendance.filter(r => r.checkInTime).length,
            checkedOut: dailyAttendance.filter(r => r.checkOutTime).length,
        };
    };

    // Combine attendance and KM data for daily view
    const getCombinedDailyData = () => {
        const dailyAttendance = getDailyAttendance();
        const dailyKm = getDailyKm();
        
        // Create a map of drivers with their data
        const driverMap = new Map();
        
        // Add attendance data
        dailyAttendance.forEach(att => {
            if (!driverMap.has(att.driverId)) {
                driverMap.set(att.driverId, {
                    driverId: att.driverId,
                    driverName: att.driverName,
                    checkInTime: null,
                    checkOutTime: null,
                    vehicle: null,
                    startKm: null,
                    endKm: null,
                    totalKm: 0,
                    status: 'absent',
                });
            }
            const driver = driverMap.get(att.driverId);
            driver.checkInTime = att.checkInTime;
            driver.checkOutTime = att.checkOutTime;
            driver.vehicle = att.vehiclePlate;
            if (att.checkInTime && att.checkOutTime) {
                driver.status = 'completed';
            } else if (att.checkInTime) {
                driver.status = 'on-duty';
            }
        });
        
        // Add KM data
        dailyKm.forEach(km => {
            if (!driverMap.has(km.driverId)) {
                driverMap.set(km.driverId, {
                    driverId: km.driverId,
                    driverName: km.driverName,
                    checkInTime: null,
                    checkOutTime: null,
                    vehicle: km.vehiclePlate,
                    startKm: null,
                    endKm: null,
                    totalKm: 0,
                    status: 'trip-only',
                });
            }
            const driver = driverMap.get(km.driverId);
            // Accumulate KM for the day
            if (!driver.startKm || km.startKm < driver.startKm) {
                driver.startKm = km.startKm;
            }
            if (!driver.endKm || km.endKm > driver.endKm) {
                driver.endKm = km.endKm;
            }
            driver.totalKm += km.totalKm;
            if (!driver.vehicle) {
                driver.vehicle = km.vehiclePlate;
            }
        });
        
        return Array.from(driverMap.values()).sort((a, b) => a.driverName.localeCompare(b.driverName));
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'completed': { bg: '#d4edda', color: '#155724', label: 'Completed' },
            'on-duty': { bg: '#cce5ff', color: '#004085', label: 'On Duty' },
            'trip-only': { bg: '#fff3cd', color: '#856404', label: 'Trip Only' },
            'absent': { bg: '#f8d7da', color: '#721c24', label: 'No Activity' },
        };
        
        const config = statusConfig[status] || statusConfig.absent;
        
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                backgroundColor: config.bg,
                color: config.color,
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500',
            }}>
                {config.label}
            </span>
        );
    };

    const stats = getStats();
    const combinedData = getCombinedDailyData();

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <div>
                        <h2 style={styles.title}>
                            <i className="fa fa-file-text-o" style={{ marginRight: '10px', color: '#1976d2' }}></i>
                            FleetP Daily Records
                        </h2>
                        <p style={styles.subtitle}>
                            View combined attendance and KM data for any day
                        </p>
                    </div>
                    <div style={styles.datePickerContainer}>
                        <label style={styles.dateLabel}>Select Date:</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={styles.datePicker}
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#e3f2fd'}}>
                        <i className="fa fa-users" style={{ fontSize: '24px', color: '#1976d2' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.totalDrivers}</div>
                        <div style={styles.statLabel}>Drivers on Duty</div>
                    </div>
                </div>
                
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#dcfce7'}}>
                        <i className="fa fa-tachometer" style={{ fontSize: '24px', color: '#22c55e' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.totalKm.toLocaleString()}</div>
                        <div style={styles.statLabel}>Total KM Logged</div>
                    </div>
                </div>
                
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#dbeafe'}}>
                        <i className="fa fa-sign-in" style={{ fontSize: '24px', color: '#2563eb' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.checkedIn}</div>
                        <div style={styles.statLabel}>Checked In</div>
                    </div>
                </div>
                
                <div style={styles.statCard}>
                    <div style={{...styles.statIcon, backgroundColor: '#fef3c7'}}>
                        <i className="fa fa-sign-out" style={{ fontSize: '24px', color: '#d97706' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{stats.checkedOut}</div>
                        <div style={styles.statLabel}>Checked Out</div>
                    </div>
                </div>
            </div>

            {/* Combined Data Table */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <h3 style={styles.cardTitle}>
                        <i className="fa fa-table" style={{ marginRight: '8px', color: '#6c757d' }}></i>
                        Daily Summary for {new Date(selectedDate).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </h3>
                    <div style={styles.recordCount}>
                        {combinedData.length} driver(s)
                    </div>
                </div>
                
                {loading ? (
                    <div style={styles.loadingState}>
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', marginRight: '10px' }}></i>
                        Loading records...
                    </div>
                ) : combinedData.length === 0 ? (
                    <div style={styles.emptyState}>
                        <i className="fa fa-calendar-o" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
                        <h4 style={{ margin: '0 0 8px 0', color: '#666' }}>No Records Found</h4>
                        <p style={{ margin: 0, color: '#999' }}>
                            No attendance or KM records for {selectedDate}
                        </p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Driver</th>
                                    <th style={styles.th}>Check-in</th>
                                    <th style={styles.th}>Check-out</th>
                                    <th style={styles.th}>Vehicle</th>
                                    <th style={styles.th}>Start KM</th>
                                    <th style={styles.th}>End KM</th>
                                    <th style={styles.th}>Total KM</th>
                                    <th style={styles.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {combinedData.map((row, index) => (
                                    <tr key={row.driverId} style={index % 2 === 0 ? {} : { backgroundColor: '#f8f9fa' }}>
                                        <td style={styles.td}>
                                            <div style={styles.driverCell}>
                                                <div style={styles.driverAvatar}>
                                                    {row.driverName.charAt(0)}
                                                </div>
                                                <div>
                                                    <strong>{row.driverName}</strong>
                                                    <div style={styles.driverId}>{row.driverId}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            {row.checkInTime ? (
                                                <span style={styles.timeValue}>{row.checkInTime}</span>
                                            ) : (
                                                <span style={styles.emptyValue}>-</span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {row.checkOutTime ? (
                                                <span style={styles.timeValue}>{row.checkOutTime}</span>
                                            ) : (
                                                <span style={styles.emptyValue}>-</span>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {row.vehicle || <span style={styles.emptyValue}>-</span>}
                                        </td>
                                        <td style={styles.td}>
                                            {row.startKm ? row.startKm.toLocaleString() : <span style={styles.emptyValue}>-</span>}
                                        </td>
                                        <td style={styles.td}>
                                            {row.endKm ? row.endKm.toLocaleString() : <span style={styles.emptyValue}>-</span>}
                                        </td>
                                        <td style={styles.td}>
                                            {row.totalKm > 0 ? (
                                                <strong style={{ color: '#1976d2' }}>
                                                    {row.totalKm.toLocaleString()} km
                                                </strong>
                                            ) : (
                                                <span style={styles.emptyValue}>-</span>
                                            )}
                                        </td>
                                        <td style={styles.td}>{getStatusBadge(row.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                
                {/* Footer with export option */}
                {combinedData.length > 0 && (
                    <div style={styles.cardFooter}>
                        <button style={styles.exportButton}>
                            <i className="fa fa-download" style={{ marginRight: '6px' }}></i>
                            Export to Excel
                        </button>
                        <button style={styles.printButton}>
                            <i className="fa fa-print" style={{ marginRight: '6px' }}></i>
                            Print Report
                        </button>
                    </div>
                )}
            </div>

            {/* Quick Navigation */}
            <div style={styles.quickNav}>
                <button 
                    onClick={() => {
                        const d = new Date(selectedDate);
                        d.setDate(d.getDate() - 1);
                        setSelectedDate(d.toISOString().split('T')[0]);
                    }}
                    style={styles.navButton}
                >
                    <i className="fa fa-chevron-left" style={{ marginRight: '6px' }}></i>
                    Previous Day
                </button>
                <button 
                    onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                    style={styles.todayButton}
                >
                    Today
                </button>
                <button 
                    onClick={() => {
                        const d = new Date(selectedDate);
                        d.setDate(d.getDate() + 1);
                        setSelectedDate(d.toISOString().split('T')[0]);
                    }}
                    style={styles.navButton}
                    disabled={selectedDate >= new Date().toISOString().split('T')[0]}
                >
                    Next Day
                    <i className="fa fa-chevron-right" style={{ marginLeft: '6px' }}></i>
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        marginBottom: '24px',
    },
    headerTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#1a1a2e',
        margin: '0 0 8px 0',
        display: 'flex',
        alignItems: 'center',
    },
    subtitle: {
        fontSize: '14px',
        color: '#666',
        margin: 0,
    },
    datePickerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: '#fff',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    dateLabel: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
    },
    datePicker: {
        padding: '8px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    statIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    statContent: {},
    statValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1a1a2e',
        lineHeight: 1.2,
    },
    statLabel: {
        fontSize: '13px',
        color: '#666',
        marginTop: '4px',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden',
    },
    cardHeader: {
        padding: '20px 24px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#333',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
    },
    recordCount: {
        fontSize: '13px',
        color: '#666',
        backgroundColor: '#f0f0f0',
        padding: '6px 12px',
        borderRadius: '16px',
    },
    tableContainer: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '14px 16px',
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontSize: '12px',
        fontWeight: '600',
        color: '#495057',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #eee',
        fontSize: '14px',
        color: '#333',
    },
    driverCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    driverAvatar: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: '#1976d2',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: '600',
    },
    driverId: {
        fontSize: '12px',
        color: '#666',
        marginTop: '2px',
    },
    timeValue: {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#333',
    },
    emptyValue: {
        color: '#ccc',
    },
    cardFooter: {
        padding: '16px 24px',
        borderTop: '1px solid #eee',
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
    },
    exportButton: {
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#fff',
        backgroundColor: '#28a745',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    printButton: {
        padding: '10px 16px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#333',
        backgroundColor: '#f0f0f0',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
    },
    loadingState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        color: '#666',
    },
    quickNav: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '24px',
    },
    navButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
    },
    todayButton: {
        padding: '10px 24px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#fff',
        backgroundColor: '#1976d2',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
    },
};
